import React, { useState, useEffect } from 'react';
import { X, MessageSquareQuote, Check, Trash2 } from 'lucide-react';
import {
    getVoiceProfile,
    setVoiceProfile,
    isVoiceEnabled,
    setVoiceEnabled,
    MAX_VOICE_SAMPLE_CHARS
} from '../services/voiceProfile.js';

const VoiceProfileModal = ({ isOpen, onClose, showNotification }) => {
    const [sample, setSample] = useState('');
    const [enabled, setEnabled] = useState(true);

    useEffect(() => {
        if (isOpen) {
            setSample(getVoiceProfile());
            setEnabled(isVoiceEnabled());
        }
    }, [isOpen]);

    const handleSave = () => {
        setVoiceProfile(sample);
        setVoiceEnabled(enabled);
        showNotification?.(sample.trim() ? 'Voice profile saved.' : 'Voice profile cleared.');
        onClose();
    };

    const handleClear = () => {
        setSample('');
        setVoiceProfile('');
        showNotification?.('Voice profile cleared.');
    };

    if (!isOpen) return null;

    const charCount = sample.length;
    const tooShort = sample.trim().length > 0 && sample.trim().length < 50;

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
                <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex justify-between items-center">
                    <h3 className="font-bold text-slate-800 flex items-center gap-2">
                        <MessageSquareQuote size={18} className="text-purple-600" />
                        Voice DNA
                    </h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6 flex-1 overflow-y-auto space-y-4">
                    <p className="text-sm text-slate-600 leading-relaxed">
                        Paste 2–3 paragraphs of your <strong>own writing</strong> — a Slack message, a blog post, an old cover letter, an email. The AI will use this as a style anchor so generated content sounds like <em>you</em>, not like generic LLM-prose.
                    </p>

                    <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-xs text-amber-900 leading-relaxed">
                        <strong>Stored on this device only.</strong> Your writing samples live in localStorage and never leave your browser except as part of AI prompts when you generate content.
                    </div>

                    <div>
                        <label className="text-xs font-bold text-slate-600 uppercase mb-2 block">
                            Your writing samples
                        </label>
                        <textarea
                            value={sample}
                            onChange={(e) => setSample(e.target.value.slice(0, MAX_VOICE_SAMPLE_CHARS))}
                            rows={10}
                            placeholder={"Paste a few paragraphs of your own writing here. Examples:\n\n- A blog post intro you wrote\n- A long Slack message explaining something\n- The body of a previous cover letter\n- A LinkedIn post\n\nThe more authentic, the better the match."}
                            className="w-full border border-slate-300 rounded-lg p-3 text-sm font-mono focus:ring-2 focus:ring-purple-500 outline-none resize-none"
                        />
                        <div className="flex justify-between text-xs mt-1">
                            <span className={tooShort ? 'text-amber-600' : 'text-slate-400'}>
                                {tooShort ? 'Add a bit more — at least 50 chars helps the AI match your voice.' : ' '}
                            </span>
                            <span className="text-slate-400 font-mono">
                                {charCount} / {MAX_VOICE_SAMPLE_CHARS}
                            </span>
                        </div>
                    </div>

                    <label className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100">
                        <div>
                            <div className="text-sm font-medium text-slate-800">Apply voice profile to generations</div>
                            <div className="text-xs text-slate-500">Turn off to use the default AI voice without disturbing your saved samples.</div>
                        </div>
                        <input
                            type="checkbox"
                            checked={enabled}
                            onChange={(e) => setEnabled(e.target.checked)}
                            className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500 border-gray-300"
                        />
                    </label>
                </div>

                <div className="p-4 border-t border-slate-200 bg-slate-50 flex items-center gap-2">
                    <button
                        onClick={handleClear}
                        disabled={!sample}
                        className="px-3 py-2 text-red-600 hover:bg-red-50 disabled:opacity-30 rounded-lg text-sm flex items-center gap-1"
                    >
                        <Trash2 size={14} /> Clear
                    </button>
                    <button onClick={onClose} className="ml-auto px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm">
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-lg text-sm flex items-center gap-2"
                    >
                        <Check size={14} /> Save
                    </button>
                </div>
            </div>
        </div>
    );
};

export default VoiceProfileModal;
